import axios from "axios";
import api from "./api";
import { isTrustedUploadUrl } from "@/utils/sanitizeUrl";
import type { PaginatedResponse } from "@/types/api.types";
import type {
  Customer,
  CustomerFormData,
  CustomerFilters,
  CRLicense,
} from "@/types/customer.types";

class CustomerService {
  async getCustomers(
    params?: CustomerFilters & { page?: number; page_size?: number },
  ): Promise<PaginatedResponse<Customer>> {
    const response = await api.get<any>("/api/v1/customers/", { params });
    // The backend returns { items: [], total: ..., page: ..., size: ... }
    // Or if I didn't change the wrapper in router, it might be { data: ... }
    // My new router returns { items: ..., total: ... } which is different from previous standard { data: ... }
    // Let's check api.types.ts to see what PaginatedResponse expects.
    // Usually standard is { data: T[], meta: ... } or similar.
    // But my router implementation:
    /*
    return {
        "items": customers,
        "total": total,
        "page": page,
        "size": page_size,
    }
    */
    // I should map this to whatever the frontend expects.
    // Assuming PaginatedResponse is { data: T[], total: number, ... }

    return {
      data: response.data.items.map(this.mapToCustomer),
      total: response.data.total,
      page: response.data.page,
      per_page: response.data.size,
      total_pages: Math.ceil(response.data.total / response.data.size),
    };
  }

  async getCustomer(id: string): Promise<Customer> {
    const response = await api.get<Customer>(`/api/v1/customers/${id}`);
    return this.mapToCustomer(response.data);
  }

  async createCustomer(data: CustomerFormData): Promise<Customer> {
    const payload = {
      name: data.name,
      phone: data.phone ?? '',
      territory: data.territory ?? null,
      name_ar: data.name_ar ?? null,
      source_lead_id: data.source_lead_id ?? null,
      customer_type: data.customer_type ?? null,
      details: data.details ?? null,
      industry: data.industry ?? null,
      website: data.website ?? null,
      daftra_client_id: data.daftra_client_id ?? null,
      erpnext_name: data.erpnext_name ?? null,
    };
    const response = await api.post<Customer>("/api/v1/customers/", payload);
    return this.mapToCustomer(response.data);
  }

  async updateCustomer(
    id: string,
    data: Partial<CustomerFormData>,
  ): Promise<Customer> {
    const payload: Record<string, unknown> = {};
    if (data.name !== undefined) payload.name = data.name;
    if (data.phone !== undefined) payload.phone = data.phone;
    if (data.territory !== undefined) payload.territory = data.territory;
    if (data.name_ar !== undefined) payload.name_ar = data.name_ar;
    if (data.source_lead_id !== undefined) payload.source_lead_id = data.source_lead_id;
    if (data.customer_type !== undefined) payload.customer_type = data.customer_type;
    if (data.details !== undefined) payload.details = data.details;
    if (data.industry !== undefined) payload.industry = data.industry;
    if (data.website !== undefined) payload.website = data.website;
    if (data.daftra_client_id !== undefined) payload.daftra_client_id = data.daftra_client_id;
    if (data.erpnext_name !== undefined) payload.erpnext_name = data.erpnext_name;
    const response = await api.put<Customer>(`/api/v1/customers/${id}`, payload);
    return this.mapToCustomer(response.data);
  }

  async deleteCustomer(id: string): Promise<void> {
    await api.delete(`/api/v1/customers/${id}`);
  }

  async searchCustomers(query: string): Promise<Customer[]> {
    const response = await api.get<any>("/api/v1/customers/search", {
      params: { q: query, limit: 10 },
    });
    return response.data.items.map(this.mapToCustomer);
  }

  async getCRLicences(params?: {
    q?: string;
    page?: number;
    page_size?: number;
  }): Promise<CRLicense[]> {
    const skip =
      params?.page && params?.page_size
        ? (params.page - 1) * params.page_size
        : 0;
    const limit = params?.page_size || 100;

    const response = await api.get<CRLicense[]>("/api/v1/customers/cr-licences/", {
      params: {
        q: params?.q,
        skip,
        limit,
      },
    });
    return response.data;
  }

  async getCRLicenseDetails(id: string): Promise<any> {
    const response = await api.get<any>(`/api/v1/customers/cr-licences/${id}`);
    return response.data;
  }

  async updateCRLicense(
    crId: string,
    data: { expiry_date?: string; vat_number?: string; company_location?: string },
  ): Promise<any> {
    const response = await api.put<any>(`/api/v1/customers/cr-licences/${crId}`, data);
    return response.data;
  }

  async getCRLicenseLocations(crId: string): Promise<any[]> {
    const response = await api.get<any[]>(`/api/v1/customers/cr-licences/${crId}/locations`);
    return Array.isArray(response.data) ? response.data : [];
  }

  async linkCRLicenseLocation(crId: string, locationId: number): Promise<void> {
    await api.post(`/api/v1/customers/cr-licences/${crId}/locations`, { location_id: locationId });
  }

  async unlinkCRLicenseLocation(crId: string, locationId: number): Promise<void> {
    await api.delete(`/api/v1/customers/cr-licences/${crId}/locations/${locationId}`);
  }

  async createCRLicense(data: {
    entity_cr_type?: string;
    company_name: string;
    company_location?: string;
    vat_number?: string;
    entity_cr: number;
    expiry_date?: string;
    main_entity_cr?: number;
    main_entity_name?: string;
  }): Promise<CRLicense> {
    const response = await api.post<any>("/api/v1/customers/cr-licences/", data);
    return response.data;
  }

  async createCRForCustomer(
    customerId: string,
    data: {
      entity_cr_type?: string;
      company_name: string;
      company_location?: string;
      vat_number?: string;
      entity_cr: number;
      expiry_date?: string;
      main_entity_cr?: number;
      main_entity_name?: string;
    }
  ): Promise<CRLicense> {
    const response = await api.post<any>(`/api/v1/customers/${customerId}/cr-licenses`, data);
    return response.data;
  }

  async unlinkLicense(customerId: string, licenseId: string): Promise<void> {
    await api.delete(`/api/v1/customers/${customerId}/licenses/${licenseId}`);
  }

  async deleteCRLicense(crId: string): Promise<void> {
    await api.delete(`/api/v1/customers/cr-licences/${crId}`);
  }

  async linkCRToCustomer(customerId: string, crLicenceId: string): Promise<void> {
    await api.post(`/api/v1/customers/${customerId}/licenses`, { license_ids: [crLicenceId] });
  }

  /**
   * CR file upload: by default multipart to the API (server → Azure). No Azure Blob CORS needed.
   * Set VITE_CR_FILE_DIRECT_TO_AZURE=true after configuring storage CORS — browser PUTs to Azure
   * (usually much faster than proxying through the API). See .env.example.
   */
  async uploadCRFile(
    crId: string,
    file: File,
    onUploadProgress?: (percent: number) => void,
  ): Promise<any> {
    const useDirectToAzure = import.meta.env.VITE_CR_FILE_DIRECT_TO_AZURE === "true";
    if (!useDirectToAzure) {
      return this.uploadCRFileViaApiProxy(crId, file, onUploadProgress);
    }

    let direct: { upload_url: string; blob_name: string } | null = null;
    try {
      const prep = await api.post<{ upload_url: string; blob_name: string }>(
        `/api/v1/customers/cr-licences/${crId}/prepare-file-upload`,
        { filename: file.name },
        { timeout: 60000 },
      );
      direct = prep.data;
    } catch {
      direct = null;
    }

    if (direct?.upload_url && direct?.blob_name) {
      if (!isTrustedUploadUrl(direct.upload_url)) {
        return this.uploadCRFileViaApiProxy(crId, file, onUploadProgress);
      }
      try {
        await axios.put(direct.upload_url, file, {
          timeout: 360000,
          headers: {
            "x-ms-blob-type": "BlockBlob",
            "Content-Type": file.type || "application/octet-stream",
          },
          onUploadProgress: onUploadProgress
            ? (e) => {
                const total = e.total ?? 0;
                const loaded = e.loaded;
                if (total > 0) {
                  onUploadProgress(Math.round((loaded * 100) / total));
                }
              }
            : undefined,
        });
      } catch {
        return this.uploadCRFileViaApiProxy(crId, file, onUploadProgress);
      }
      const done = await api.post<any>(
        `/api/v1/customers/cr-licences/${crId}/complete-file-upload`,
        {
          blob_name: direct.blob_name,
          filename: file.name,
          content_type: file.type || "application/octet-stream",
        },
        { timeout: 60000 },
      );
      if (onUploadProgress) onUploadProgress(100);
      return done.data;
    }

    return this.uploadCRFileViaApiProxy(crId, file, onUploadProgress);
  }

  /** Legacy: multipart to API (server uploads to Azure). Slower; used when direct upload is unavailable. */
  private async uploadCRFileViaApiProxy(
    crId: string,
    file: File,
    onUploadProgress?: (percent: number) => void,
  ): Promise<any> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post<any>(
      `/api/v1/customers/cr-licences/${crId}/files`,
      formData,
      {
        // Do not set Content-Type manually (no boundary). Clear axios default `application/json`
        // so the browser sets multipart/form-data with boundary — required for FastAPI File().
        timeout: 360000,
        transformRequest: [
          (data, headers) => {
            if (typeof FormData !== "undefined" && data instanceof FormData) {
              delete (headers as Record<string, unknown>)["Content-Type"];
            }
            return data;
          },
        ],
        onUploadProgress: onUploadProgress
          ? (e) => {
              const total = e.total ?? 0;
              const loaded = e.loaded;
              if (total > 0 && loaded >= total) {
                onUploadProgress(-1);
                return;
              }
              const percent = total ? Math.round((loaded * 100) / total) : 0;
              onUploadProgress(Math.min(percent, 90));
            }
          : undefined,
      },
    );
    return response.data;
  }

  async getCRFileBlob(crId: string, fileId: string): Promise<Blob> {
    const response = await api.get(
      `/api/v1/customers/cr-licences/${crId}/files/${fileId}/download`,
      { responseType: "blob" },
    );
    const contentType = response.headers["content-type"];
    return new Blob([response.data], {
      type: typeof contentType === "string" ? contentType : "application/octet-stream",
    });
  }

  async openCRFileInBrowser(crId: string, fileId: string): Promise<void> {
    const blob = await this.getCRFileBlob(crId, fileId);
    const url = window.URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  }

  async deleteCRFile(crId: string, fileId: string): Promise<void> {
    await api.delete(
      `/api/v1/customers/cr-licences/${crId}/files/${fileId}`,
    );
  }

  async downloadCRFile(crId: string, fileId: string, fileName: string): Promise<void> {
    const blob = await this.getCRFileBlob(crId, fileId);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName || "file");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  async getContacts(customerId: string): Promise<any[]> {
    const response = await api.get<any[]>(
      `/api/v1/customers/${customerId}/contacts`,
    );
    return response.data;
  }

  async createContact(customerId: string, data: any): Promise<any> {
    // Blank optionals must be omitted: the backend types them as EmailStr/phone-pattern
    // and rejects "" (A2-2).
    const payload = { ...(data ?? {}) }
    for (const key of ['email', 'phone']) {
      const value = payload[key]
      if (typeof value === 'string' && !value.trim()) {
        delete payload[key]
      }
    }
    const response = await api.post<any>(
      `/api/v1/customers/${customerId}/contacts`,
      payload,
    );
    return response.data;
  }

  async getContracts(customerId: string): Promise<any[]> {
    const response = await api.get<any[]>(
      `/api/v1/customers/${customerId}/contracts`,
    );
    return response.data;
  }

  async getQuotations(customerId: string): Promise<any[]> {
    const response = await api.get<any[]>(
      `/api/v1/customers/${customerId}/quotations`,
    );
    return response.data;
  }

  async getCustomerLicenses(customerId: string): Promise<any[]> {
    const response = await api.get<any>(
      `/api/v1/customers/${customerId}/licenses`,
    );
    return Array.isArray(response.data) ? response.data : response.data?.data ?? [];
  }

  async getLocations(customerId: string): Promise<any[]> {
    const response = await api.get<any>(
      `/api/v1/customers/${customerId}/locations`,
    );
    return Array.isArray(response.data) ? response.data : response.data?.data ?? [];
  }

  async linkLocation(customerId: string, locationId: number): Promise<any> {
    const response = await api.post(`/api/v1/customers/${customerId}/locations`, {
      location_id: locationId,
    });
    return response.data;
  }

  async createLocationAndLink(
    customerId: string,
    data: {
      name: string
      address?: string
      city?: string
      state?: string
      country?: string
      postal_code?: string
      address_type_id?: number
    }
  ): Promise<any> {
    const response = await api.post(`/api/v1/customers/${customerId}/locations`, {
      name: data.name,
      address: data.address,
      city: data.city,
      state: data.state,
      country: data.country,
      postal_code: data.postal_code,
      address_type_id: data.address_type_id,
    });
    return response.data;
  }

  async unlinkLocation(customerId: string, locationId: number): Promise<void> {
    await api.delete(`/api/v1/customers/${customerId}/locations/${locationId}`);
  }

  async linkLicense(
    customerId: string,
    licenseIds: string[],
  ): Promise<Customer> {
    const response = await api.post<Customer>(
      `/api/v1/customers/${customerId}/licenses`,
      {
        license_ids: licenseIds,
      },
    );
    return this.mapToCustomer(response.data);
  }

  async exportCustomers(): Promise<void> {
    const response = await api.get("/api/v1/customers/export", {
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "customers_export.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  async importCustomers(file: File): Promise<any> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post("/api/v1/customers/import", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  }

  /** Normalize a customer JSON payload from any API (ids may be `id` or `cus_id`, etc.). */
  fromApiCustomer(data: unknown): Customer {
    return this.mapToCustomer(data as Record<string, unknown>);
  }

  private mapToCustomer(data: any): Customer {
    return {
      id: String(data.customer_id ?? data.cus_id ?? data.id ?? ""),
      name: data.customer_name ?? data.cus_name ?? data.name,
      phone: data.phone_number ?? data.cus_phone ?? data.phone ?? '',
      territory: data.territory ?? data.cus_territory ?? null,
      name_ar: data.name_ar ?? data.cus_name_ar ?? null,
      source_lead_id: data.source_lead_id ?? data.cus_source_lead_id ?? null,
      source_lead_name: data.source_lead_name ?? null,
      source_lead_code: data.source_lead_code ?? null,
      customer_type: data.customer_type ?? data.cus_customer_type ?? null,
      details: data.details ?? data.cus_details ?? null,
      industry: data.industry ?? data.cus_industry ?? null,
      website: data.website ?? data.cus_website ?? null,
      daftra_client_id: data.daftra_client_id ?? data.cus_daftra_client_id ?? null,
      erpnext_name: data.erpnext_name ?? data.cus_erpnext_name ?? null,
      created_at: data.cus_created_at || data.created_at,
      created_by: data.cus_created_by || data.created_by,
      tenant_id: data.cus_tenant_id || data.tenant_id,
      license_ids: Array.isArray(data.license_ids) ? data.license_ids.map(String) : [],
    };
  }
}

export default new CustomerService();
